import { DENSITY_METRICS, type DensityBudget, type DensityDefinition, type DensityReach } from "./density.ts";
import type { EntityTypeDefinition } from "./schema.ts";

/**
 * The checks on a game's density registration (ADR-088 decision 4), run by `registerAdapter` so a bad budget or
 * group fails when the editor starts rather than as a wrong reading.
 */

const GROUP_ID = /^[a-z0-9-]+$/;

/**
 * Checks one budget: whole thresholds with `0 < amber <= red`.
 *
 * @param metric The metric's id, for the message.
 * @param budget The budget.
 * @throws Error naming `density.budgets` and the metric.
 */
function validateBudget(metric: string, budget: DensityBudget): void {
  const { amber, red } = budget;
  if (!Number.isInteger(amber) || !Number.isInteger(red) || amber <= 0 || amber > red) {
    throw new Error(`density.budgets: ${metric} has amber ${amber} and red ${red}; give whole numbers with 0 < amber <= red`);
  }
}

/**
 * Checks a group's reach: absent, `chunks`, or around the view with a whole margin of 0 or more.
 *
 * @param id The group's id, for the message.
 * @param reach The reach.
 * @throws Error naming `density.groups` and the group.
 */
function validateReach(id: string, reach: DensityReach | undefined): void {
  if (reach === undefined || reach === "chunks") {
    return;
  }
  const margin = typeof reach === "object" && reach !== null && typeof reach.aroundView === "object" && reach.aroundView !== null ? reach.aroundView.marginTiles : undefined;
  if (margin === undefined) {
    throw new Error(`density.groups: ${id} reaches ${String(reach)}; use chunks or { aroundView: { marginTiles } }`);
  }
  if (!Number.isInteger(margin) || margin < 0) {
    throw new Error(`density.groups: ${id} has a margin of ${margin} tiles; give a whole number of 0 or more`);
  }
}

/**
 * Checks a game's density registration against its entity types.
 *
 * @param density The registration.
 * @param entityTypes The game's entity types.
 * @param camera Whether the game registers a camera, whose view the meter measures.
 * @throws Error naming the rule broken: no camera; a group id not of lower-case letters, digits and `-`,
 *   repeated or a built-in metric's; a blank label; a group listing no types, an unregistered type or a type
 *   another group lists; a count field not an `integer` property of every type in its group; a budget for an
 *   unknown metric or with thresholds outside `0 < amber <= red`; a reach that is neither `chunks` nor around the
 *   view with a whole margin of 0 or more; a view that is not whole positive pixels.
 */
export function validateDensity(density: DensityDefinition, entityTypes: readonly EntityTypeDefinition[], camera: boolean): void {
  if (!camera) {
    throw new Error("density needs a registered camera, whose view the meter measures");
  }
  const builtIn = new Set(DENSITY_METRICS.map((metric) => metric.id));
  const definitions = new Map(entityTypes.map((definition) => [definition.type, definition]));
  const groupIds = new Set<string>();
  const owner = new Map<string, string>();
  for (const group of density.groups) {
    if (typeof group.id !== "string" || !GROUP_ID.test(group.id)) {
      throw new Error(`density.groups: ${String(group.id)} is not an id of lower-case letters, digits and -`);
    }
    if (builtIn.has(group.id)) {
      throw new Error(`density.groups: ${group.id} is a built-in metric; choose another id`);
    }
    if (groupIds.has(group.id)) {
      throw new Error(`density.groups: ${group.id} is registered twice`);
    }
    groupIds.add(group.id);
    if (typeof group.label !== "string" || group.label.trim() === "") {
      throw new Error(`density.groups: ${group.id} has an empty label`);
    }
    if (group.types.length === 0) {
      throw new Error(`density.groups: ${group.id} lists no entity types`);
    }
    for (const type of group.types) {
      const definition = definitions.get(type);
      if (definition === undefined) {
        throw new Error(`density.groups: ${group.id} lists ${type}, which is not a registered entity type`);
      }
      const other = owner.get(type);
      if (other !== undefined) {
        throw new Error(`density.groups: ${type} is in ${other} and in ${group.id}; a type counts in one group`);
      }
      owner.set(type, group.id);
      if (group.countField !== undefined && definition.schema.properties[group.countField]?.type !== "integer") {
        throw new Error(`density.groups: ${group.id} counts by ${group.countField}, which ${type} does not declare as an integer`);
      }
    }
  }
  for (const group of density.groups) {
    validateReach(group.id, group.reach);
  }
  const view = density.view;
  if (view !== undefined && (!Number.isInteger(view.width) || !Number.isInteger(view.height) || view.width <= 0 || view.height <= 0)) {
    throw new Error(`density.view is ${view.width} by ${view.height}; give whole positive pixels`);
  }
  for (const [metric, budget] of Object.entries(density.budgets)) {
    if (!builtIn.has(metric) && !groupIds.has(metric)) {
      throw new Error(`density.budgets: ${metric} is not a metric; budget one of ${[...builtIn, ...groupIds].join(", ")}`);
    }
    validateBudget(metric, budget);
  }
}
